/**
 * Organization Service
 *
 * 組織管理服務（使用 Supabase）
 */

import accessService from '@shared/services/auth/accessService'
import relationshipService from '@shared/services/organization/relationshipService'
import { supabase } from '@shared/services/core/supabaseClient'
import { nanoid } from 'nanoid'
import type { Session, PostgrestError } from '@supabase/supabase-js'

// ==================== 類型定義 ====================

/** 組織狀態 */
export type OrganizationStatus = 'active' | 'inactive' | 'suspended' | 'deleted'

/** 組織資料庫 row */
export interface OrganizationRow {
  id: string
  name: string
  description?: string | null
  type?: string | null
  status: OrganizationStatus
  tax_id?: string | null
  address?: string | null
  phone?: string | null
  email?: string | null
  focus_fields?: string | string[] | null
  created_by?: string | null
  created_at?: string
  updated_at?: string
  deleted_at?: string | null
}

/** 建立組織參數 */
export interface CreateOrganizationParams {
  name: string
  description?: string
  type?: string | null
}

/** 更新組織參數 */
export interface UpdateOrganizationParams {
  name?: string
  description?: string
  type?: string | null
  tax_id?: string | null
  address?: string | null
  phone?: string | null
  email?: string | null
}

/** 建立組織結果 */
export interface CreateOrganizationResult {
  id: string
  name?: string
  description?: string
  type?: string | null
  createdAt: string
  createdBy: string
}

/** Supabase error code for no rows */
const NO_ROWS_ERROR_CODE = 'PGRST116'

// ==================== 輔助函數 ====================

/**
 * 解析 focus_fields 欄位
 */
function parseFocusFields(focusFields: string | string[] | null | undefined): string[] {
  if (!focusFields) return []

  if (typeof focusFields === 'string') {
    try {
      return JSON.parse(focusFields)
    } catch (e) {
      console.warn('解析 focus_fields 失敗:', e)
      return []
    }
  }

  if (Array.isArray(focusFields)) {
    return focusFields
  }

  return []
}

/**
 * 確保 organization 的 focus_fields 被正確解析
 */
function normalizeOrganization(org: OrganizationRow): OrganizationRow {
  return {
    ...org,
    focus_fields: parseFocusFields(org.focus_fields)
  }
}

// ==================== Service 實作 ====================

export const organizationService = {
  /**
   * 創建新組織
   */
  async createOrganization(orgData: CreateOrganizationParams): Promise<CreateOrganizationResult> {
    // 檢查 Supabase 認證
    const { data: { session }, error: sessionError } = await supabase.auth.getSession()

    if (sessionError) {
      console.error('[organizationService] 獲取 Supabase session 失敗:', sessionError)
      throw new Error('用戶未認證')
    }

    if (!session?.user) {
      throw new Error('用戶未認證')
    }

    try {
      const now = new Date().toISOString()
      const orgId = nanoid()

      const insertData: Partial<OrganizationRow> = {
        id: orgId,
        name: orgData.name || '',
        description: orgData.description || '',
        type: orgData.type || null,
        status: 'active',
        created_by: session.user.id,
        created_at: now,
        updated_at: now
      }

      const { error } = await supabase
        .from('organizations')
        .insert(insertData)
        .select()
        .single()

      if (error) throw error

      // 將創建者設置為組織的擁有者
      await relationshipService.setRole(orgId, '', session.user.id, ['owner', 'admin', 'editor'])

      return {
        id: orgId,
        ...orgData,
        createdAt: now,
        createdBy: session.user.id
      }
    } catch (error) {
      console.error('創建組織失敗：', error)
      throw error
    }
  },

  /**
   * 獲取組織信息
   */
  async getOrganization(orgId: string): Promise<OrganizationRow | null> {
    if (!orgId) return null

    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .is('deleted_at', null)
        .single()

      if (error) {
        if ((error as PostgrestError).code === NO_ROWS_ERROR_CODE) {
          // 沒有找到記錄
          return null
        }
        throw error
      }

      if (data && data.name) {
        return normalizeOrganization(data as OrganizationRow)
      }
      return null
    } catch (e) {
      console.error(`Organization ${orgId} not found:`, e)
      return null
    }
  },

  /**
   * 更新組織信息
   */
  async updateOrganization(orgId: string, updateData: UpdateOrganizationParams): Promise<boolean> {
    // 檢查 Supabase 認證
    const { data: { session }, error: sessionError } = await supabase.auth.getSession()

    if (sessionError) {
      console.error('[organizationService] 獲取 Supabase session 失敗:', sessionError)
      throw new Error('用戶未認證')
    }

    if (!session?.user) {
      throw new Error('用戶未認證')
    }

    // 檢查用戶是否有權限編輯組織信息
    if (!(await accessService.canPerformAction('org:edit_basic_info', orgId, null, session.user.id))) {
      throw new Error('權限不足：無法編輯組織信息')
    }

    try {
      const now = new Date().toISOString()
      const updatePayload = {
        name: updateData.name,
        description: updateData.description,
        type: updateData.type,
        tax_id: updateData.tax_id,
        address: updateData.address,
        phone: updateData.phone,
        email: updateData.email,
        updated_at: now
      }

      const { data, error } = await supabase
        .from('organizations')
        .update(updatePayload)
        .eq('id', orgId)
        .is('deleted_at', null)
        .select()

      if (error) throw error

      return data !== null && data.length > 0
    } catch (error) {
      console.error('更新組織信息時發生錯誤:', error)
      throw error
    }
  },

  /**
   * 刪除組織（軟刪除）
   */
  async deleteOrganization(orgId: string): Promise<boolean> {
    try {
      const now = new Date().toISOString()
      const { data, error } = await supabase
        .from('organizations')
        .update({
          deleted_at: now,
          status: 'deleted'
        })
        .eq('id', orgId)
        .is('deleted_at', null)
        .select()

      if (error) throw error

      return data !== null && data.length > 0
    } catch (error) {
      console.error('刪除組織時發生錯誤:', error)
      throw error
    }
  },

  /**
   * 批量獲取組織信息
   */
  async getOrganizationByIds(idArray: string[]): Promise<OrganizationRow[]> {
    if (!idArray || !Array.isArray(idArray) || idArray.length === 0) {
      return []
    }

    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .in('id', idArray)
        .is('deleted_at', null)

      if (error) throw error

      if (data && Array.isArray(data)) {
        const organizations = (data as OrganizationRow[])
          .filter(org => org != null)
          .map(org => normalizeOrganization(org))

        // 按照輸入的 ID 數組順序排序結果
        const resultMap = new Map(organizations.map(org => [org.id, org]))
        return idArray
          .map(id => resultMap.get(id))
          .filter((org): org is OrganizationRow => org != null)
      }

      return []
    } catch (error) {
      console.error('批量獲取組織信息失敗：', error)
      return []
    }
  },

  /**
   * 更新組織的焦點案場列表
   */
  async updateFocusFields(org: { id: string }, newFocusFields: string[]): Promise<string[]> {
    // 檢查 Supabase 認證
    const { data: { session }, error: sessionError } = await supabase.auth.getSession()

    if (sessionError) {
      console.error('[organizationService] 獲取 Supabase session 失敗:', sessionError)
      throw new Error('用戶未認證')
    }

    if (!session?.user) {
      throw new Error('用戶未認證')
    }

    try {
      const now = new Date().toISOString()
      console.log('updateFocusFields', org.id, newFocusFields)

      const { error } = await supabase
        .from('organizations')
        .update({
          focus_fields: newFocusFields,
          updated_at: now
        })
        .eq('id', org.id)
        .is('deleted_at', null)
        .select()

      if (error) throw error

      return newFocusFields
    } catch (error) {
      console.error('更新焦點案場失敗：', error)
      throw error
    }
  }
}

export default organizationService
